// fetchDb.ts
const apiUrl = import.meta.env.VITE_API_URL;
type RequestType = "POST" | "PATCH" | "DELETE" | "GET";

interface PendingRequest {
   timer: number;
   resolve: (value: any) => void;
   reject: (reason?: any) => void;
}

const DEBOUNCE_INTERVAL = 500; // 500 milliseconds

const fetchDb = (() => {
   // Map to store pending debounced requests
   const pendingRequests: Map<string, PendingRequest> = new Map();
   // Helper function to create a unique key based on method and URL
   const createKey = (method: RequestType, url: string): string => {
      return `${method}:${url}`;
   };

   // The main request function with debouncing for PATCH and DELETE
   const request = <T = any>(
      oldUrl: string,
      method: RequestType,
      body?: any
   ): Promise<T> => {
      const url = oldUrl.replace("/api/", apiUrl);

      // For PATCH and DELETE, implement debouncing
      if (method === "PATCH" || method === "DELETE") {
         const key = createKey(method, url);

         // If a similar request is already pending, clear the previous timer and
         // reject its promise
         if (pendingRequests.has(key)) {
            const existingRequest = pendingRequests.get(key);
            if (existingRequest) {
               clearTimeout(existingRequest.timer);
               existingRequest.reject({
                  message: "Request cancelled due to new request",
                  type: 120,
               });
            }
         }

         return new Promise<T>((resolve, reject) => {
            // Set a new timeout to execute the request after the debounce interval
            const timer = window.setTimeout(async () => {
               try {
                  const options: RequestInit = {
                     method,
                     headers: {
                        "Content-Type": "application/json",
                     },
                  };
                  if (body) {
                     options.body = JSON.stringify(body);
                  }

                  const response = await fetch(url, options);

                  if (!response.ok) {
                     throw new Error(
                        `Error ${response.status}: ${response.statusText}`
                     );
                  }

                  const data = await response.json();
                  resolve(data);
               } catch (error) {
                  reject(error);
               } finally {
                  // Remove the request from the pending map after completion
                  pendingRequests.delete(key);
               }
            }, DEBOUNCE_INTERVAL);

            // Store the new pending request
            pendingRequests.set(key, { timer, resolve, reject });
         });
      } else if (method === "POST" && body instanceof FormData) {
         return new Promise<T>(async (resolve, reject) => {
            try {
               const response = await fetch(url, { method, body });
               console.log("response", response);
               if (!response.ok) {
                  throw new Error(
                     `Error ${response.status}: ${response.statusText}`
                  );
               }
               const data = await response.json();
               resolve(data);
            } catch (error) {
               reject(error);
            }
         });
      } else {
         // For GET and POST, execute immediately
         return new Promise<T>(async (resolve, reject) => {
            try {
               const options: RequestInit = {
                  method,
                  headers: {
                     "Content-Type": "application/json",
                  },
               };

               if (body && method === "POST") {
                  options.body = JSON.stringify(body);
               }

               const response = await fetch(url, options);

               if (!response.ok) {
                  throw new Error(
                     `Error ${response.status}: ${response.statusText}`
                  );
               }
               console.log("response", response);
               const data = await response.json();
               resolve(data);
            } catch (error) {
               reject(error);
            }
         });
      }
   };

   // Define the fetchDb object with methods
   return {
      async request<T = any>(
         url: string,
         method: RequestType,
         body?: any
      ): Promise<T> {
         return request<T>(url, method, body);
      },

      async post<T = any>(url: string, body: any): Promise<T> {
         return request<T>(url, "POST", body);
      },

      async get<T = any>(url: string): Promise<T> {
         return request<T>(url, "GET");
      },

      async patch<T = any>(url: string, body: any = undefined): Promise<T> {
         return request<T>(url, "PATCH", body);
      },

      async delete<T = any>(url: string): Promise<T> {
         return request<T>(url, "DELETE");
      },
      async postFormData<T = any>(url: string, body: FormData): Promise<T> {
         return request<T>(url, "POST", body);
      },
   };
})();
export default fetchDb;
